// src/services/teacherService.js
import { db, auth } from '../firebase.js';
import {
  collection, doc, setDoc, getDocs, deleteDoc, query,
  orderBy, serverTimestamp, getDoc, updateDoc
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword, deleteUser
} from 'firebase/auth';

const teachersCol = collection(db, 'teachers');
const usersCol    = collection(db, 'users');   // coleção raiz

/* ───────────────────────── config ───────────────────────── */
const DEFAULT_INITIAL_CREDITS = 2;

/* ───────────────────────── listagem ───────────────────────── */
export const getAllTeachers = async () => {
  try {
    const q  = query(teachersCol, orderBy('name'));
    const qs = await getDocs(q);
    return qs.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('Error fetching teachers', err);
    throw new Error('Failed to fetch teachers.');
  }
};

/* ───────────────────────── criação ─────────────────────────
   Agora aceita `initialCredits` e grava os campos de crédito SOMENTE em users/{uid}.
*/
export const addTeacher = async ({ email, password, name, initialCredits }) => {
  if (!email || !password || !name)
    throw new Error('Missing required teacher data.');

  // normaliza créditos iniciais
  const creditsInit = Number.isFinite(+initialCredits)
    ? Math.max(0, Math.floor(+initialCredits))
    : DEFAULT_INITIAL_CREDITS;

  let authUser = null;
  try {
    /* 1 ▪ Auth */
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    authUser   = cred.user;
    const uid  = authUser.uid;

    const now = serverTimestamp();

    /* 2 ▪ users/{uid} — créditos iniciais (fonte única da verdade) */
    await setDoc(doc(usersCol, uid), {
      name,
      email,
      role: 'teacher',
      createdAt: now,
      // ── Créditos (SOMENTE em users) ──
      credits: creditsInit,      // créditos restantes
      creditsTotal: creditsInit, // total concedido na criação
      creditsUsed: 0,            // começa em 0
      creditsUpdatedAt: now,
    });

    /* 3 ▪ teachers/{uid} — sem campos de crédito */
    await setDoc(doc(teachersCol, uid), {
      name,
      email,
      createdAt: now,
      createdClassroomIds: []
    });

    return uid;
  } catch (err) {
    console.error('Error adding teacher', err);
    if (authUser) try { await deleteUser(authUser); } catch (_) {}
    throw translateAuthError(err);
  }
};

/* ───────────────────────── leitura ───────────────────────── */
export const getTeacherById = async (uid) => {
  const snap = await getDoc(doc(teachersCol, uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/* ───────────────────────── atualização ───────────────────────── */
export const updateTeacher = async (uid, data) => {
  try {
    const common = {};
    if (Object.prototype.hasOwnProperty.call(data, 'name'))  common.name  = data.name;
    if (Object.prototype.hasOwnProperty.call(data, 'email')) common.email = data.email;

    if (Object.keys(common).length)
      await updateDoc(doc(usersCol, uid), { ...common, updatedAt: serverTimestamp() });

    await updateDoc(doc(teachersCol, uid), {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (err) {
    console.error('Error updating teacher', err);
    throw new Error('Failed to update teacher.');
  }
};

/* ───────────────────────── exclusão ───────────────────────── */
export const deleteTeacher = async (uid) => {
  try {
    await Promise.all([
      deleteDoc(doc(teachersCol, uid)),
      deleteDoc(doc(usersCol,     uid))
    ]);
    // a remoção do Auth deve acontecer via Cloud Function administrativa
  } catch (err) {
    console.error('Error deleting teacher', err);
    throw new Error('Failed to delete teacher.');
  }
};

/* ───────────────────────── helpers de classroom (inalterados) ───────────────────────── */
// Adiciona o ID da sala criada na lista do professor
export const addCreatedClassroomToTeacher = async (teacherId, classroomId) => {
  if (!teacherId || !classroomId) return;
  try {
    const ref  = doc(teachersCol, teacherId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const list = snap.data().createdClassroomIds || [];
      if (!list.includes(classroomId)) {
        await updateDoc(ref, { createdClassroomIds: [...list, classroomId] });
      }
    }
  } catch (err) {
    console.error('Error adding classroom ID to teacher:', err);
  }
};

// Remove o ID da sala (p. ex. ao deletar a classroom)
export const removeCreatedClassroomFromTeacher = async (teacherId, classroomId) => {
  if (!teacherId || !classroomId) return;
  try {
    const ref  = doc(teachersCol, teacherId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const list     = snap.data().createdClassroomIds || [];
      const filtered = list.filter(id => id !== classroomId);
      await updateDoc(ref, { createdClassroomIds: filtered });
    }
  } catch (err) {
    console.error('Error removing classroom ID from teacher:', err);
  }
};

function translateAuthError(err){
  if (err.code === 'auth/email-already-in-use')
    return new Error('E-mail já registrado.');
  if (err.code === 'auth/weak-password')
    return new Error('Senha fraca (mínimo 6 caracteres).');
  return new Error('Falha ao criar professor.');
}
